var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import { apiRequestId } from '../../api';
import { READ_CACHE_POLICIES } from '../../api/constants';
import { ApiContext } from '../context';
import { useApiQueryActions } from './actions';
import { INITIAL_STATE, useApiQueryReducer } from './reducer';
/**
 * API hook to run an api `GET` request, returning a `Loadable`
 * instance of the response data
 */
export function useApiQuery(params, opts = {}) {
    var _a, _b;
    const { api, defaults: { useApiQuery: { fetchPolicy: defaultFetchPolicy, useErrorBoundary: defaultUseErrorBoundary } } } = useContext(ApiContext);
    const fetchPolicy = (_a = opts.fetchPolicy, (_a !== null && _a !== void 0 ? _a : defaultFetchPolicy));
    const useErrorBoundary = (_b = opts.useErrorBoundary, (_b !== null && _b !== void 0 ? _b : defaultUseErrorBoundary));
    const [state, dispatch] = useReducer(useApiQueryReducer, INITIAL_STATE);
    const paramsId = params ? apiRequestId(params) : null;
    /**
     * Used to identify the current request for concurrency management
     */
    const requestId = Symbol();
    /**
     * True if `paramsId` just changed, but `useEffect` hasn't triggered yet.
     */
    const paramsIdChanged = useValueChanged(paramsId);
    /**
     * If we're about to kick off a new request, and the `fetchPolicy` allows
     * reading from the cache, read the cached data.
     */
    const cachedData = paramsIdChanged && params && READ_CACHE_POLICIES.includes(fetchPolicy)
        ? api.readCachedResponse(params)
        : null;
    /**
     * If params id changes, but `useEffect` hasn't kicked off yet,
     * derive an intermediary state via the reducer.
     */
    const derivedState = paramsIdChanged
        ? useApiQueryReducer(state, useApiQueryActions.newRequest({
            requestId,
            paramsId,
            fetchPolicy,
            initialData: opts.initialData,
            cachedData,
            dontReinitialize: opts.dontReinitialize
        }))
        : state;
    /**
     * When `paramsId` changes
     * - Update reducer state with the derived state for the new request
     * - Sunscribe to cache updates
     * - Make the request
     */
    useEffect(() => {
        dispatch(useApiQueryActions.replaceState(derivedState));
        if (!params) {
            return undefined;
        }
        const subscription = api.onCacheUpdate(params).subscribe((responseBody) => {
            dispatch(useApiQueryActions.setData(responseBody));
        });
        const cleanup = () => subscription.unsubscribe();
        if (fetchPolicy === 'cache-only' && cachedData) {
            // no need to make a request
            return cleanup;
        }
        ;
        (function requestQueryData() {
            return __awaiter(this, void 0, void 0, function* () {
                try {
                    const data = yield api.request(params, {
                        fetchPolicy,
                        deduplicate: opts.deduplicate
                    });
                    dispatch(useApiQueryActions.success({
                        requestId: requestId,
                        paramsId: paramsId,
                        data
                    }));
                }
                catch (error) {
                    dispatch(useApiQueryActions.failure({
                        requestId: requestId,
                        paramsId: paramsId,
                        error
                    }));
                }
            });
        })();
        return cleanup;
    }, [paramsId]);
    /**
     * Keep referential equality and only change if underlying
     * `loading`, `data`, or `error` state changes
     */
    const returnData = useMemo(() => {
        return {
            loading: derivedState.loading,
            data: derivedState.data,
            error: derivedState.error
        };
    }, [derivedState.loading, derivedState.data, derivedState.error]);
    /**
     * Optionally throw the error to handle in error boundary
     */
    if (returnData.error && useErrorBoundary) {
        throw returnData.error;
    }
    const handleSetData = (data) => {
        if (fetchPolicy === 'no-cache' || !params) {
            dispatch(useApiQueryActions.setData(data));
        }
        else {
            // write to the cache, which will in turn
            // notify the subscription and update `state.data`
            if (typeof data === 'function') {
                // function setter
                const prev = api.readCachedResponse(params);
                api.writeCachedResponse(params, data(prev));
            }
            else {
                api.writeCachedResponse(params, data);
            }
        }
    };
    const handleRefetch = (refetchOpts = {}) => __awaiter(this, void 0, void 0, function* () {
        var _c;
        if (!paramsId || !params) {
            return;
        }
        const requestId = Symbol();
        dispatch(useApiQueryActions.refetchRequest({
            requestId,
            paramsId,
            reinitialize: refetchOpts.reinitialize
        }));
        try {
            const data = yield api.request(params, {
                fetchPolicy: fetchPolicy === 'no-cache' ? 'no-cache' : 'fetch-first',
                deduplicate: (_c = refetchOpts.deduplicate, (_c !== null && _c !== void 0 ? _c : false))
            });
            dispatch(useApiQueryActions.success({ requestId, paramsId, data }));
        }
        catch (error) {
            dispatch(useApiQueryActions.failure({ requestId, paramsId, error }));
        }
    });
    return [returnData, { setData: handleSetData, refetch: handleRefetch }];
}
/**
 * Returns true if the value changed since last render (true on first pass)
 */
function useValueChanged(value) {
    const ref = useRef(null);
    useEffect(() => {
        ref.current = value;
    }, [value]);
    return ref.current !== value;
}
//# sourceMappingURL=index.js.map